/**
 * 권한 관리 API 클라이언트
 */

import axiosInstance from '../lib/axios';
import type {
  RolePermissionResponse,
  RolePermissionUpdateRequest,
  Permission,
  UserFunctionPermissions,
  ApiResponse,
} from '../types/permission.types';

/**
 * 권한 API 클라이언트
 */
export class PermissionApiClient {
  /**
   * 메뉴 계층 구조와 함께 역할 권한 조회
   * @param roleId 역할 ID
   * @returns 역할 권한 응답
   */
  static async getRolePermissions(roleId: number): Promise<RolePermissionResponse> {
    try {
      const response = await axiosInstance.get<ApiResponse<RolePermissionResponse>>(
        `/permissions/roles/${roleId}`
      );
      return response.data.data;
    } catch (error) {
      console.error('역할 권한 조회 실패:', error);
      throw new Error('역할 권한을 불러오는 중 오류가 발생했습니다.');
    }
  }

  /**
   * 역할 권한 업데이트 (일괄 업데이트)
   * @param request 권한 업데이트 요청
   */
  static async updateRolePermissions(request: RolePermissionUpdateRequest): Promise<void> {
    try {
      await axiosInstance.put(`/permissions/roles`, request);
    } catch (error) {
      console.error('역할 권한 업데이트 실패:', error);
      throw new Error('권한 저장 중 오류가 발생했습니다.');
    }
  }

  /**
   * 사용 가능한 모든 권한 조회
   * @returns 권한 목록
   */
  static async getAllPermissions(): Promise<Permission[]> {
    try {
      const response = await axiosInstance.get<ApiResponse<Permission[]>>(`/permissions/all`);
      return response.data.data;
    } catch (error) {
      console.error('권한 목록 조회 실패:', error);
      throw new Error('권한 목록을 불러오는 중 오류가 발생했습니다.');
    }
  }

  /**
   * 역할이 특정 권한을 가지고 있는지 확인
   * @param roleId 역할 ID
   * @param menuId 메뉴 ID
   * @param action 권한 액션
   * @returns 권한 보유 여부
   */
  static async checkPermission(roleId: number, menuId: number, action: string): Promise<boolean> {
    try {
      const response = await axiosInstance.get<ApiResponse<boolean>>(`/permissions/check`, {
        params: { roleId, menuId, action },
      });
      return response.data.data;
    } catch (error) {
      console.error('권한 확인 실패:', error);
      return false;
    }
  }

  /**
   * 사용자의 기능 권한을 permission_key → boolean 맵으로 조회
   * @param userId 사용자 ID
   * @returns permission_key → boolean 맵
   */
  static async getUserFunctionPermissions(userId: number): Promise<UserFunctionPermissions> {
    try {
      const response = await axiosInstance.get<ApiResponse<UserFunctionPermissions>>(
        `/permissions/users/${userId}`
      );
      return response.data.data;
    } catch (error) {
      console.error('사용자 기능 권한 조회 실패:', error);
      throw new Error('사용자 기능 권한을 불러오는 중 오류가 발생했습니다.');
    }
  }
}
